import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Typography, Tabs, Tab, Paper, Stack, Divider, Alert, Button, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from 'notistack';
import { UserContext } from '../../context/UserContext';
import { StationContext } from '../../context/StationContext';
import { hasAnyRequiredPermission, STAFFING_PAGE_PERMISSIONS, STAFFING_WRITE_PERMISSIONS } from '../../../config/routePermissions';
import useStaffingData from './useStaffingData';
import StaffingMatrix from './StaffingMatrix';
import StaffingExceptions from './StaffingExceptions';
import StaffingMonthPreview from './StaffingMonthPreview';
import StaffingRecalcDialog from './StaffingRecalcDialog';
import { mergeRecalcRanges, describeRecalcRange } from './staffingUtils';

// Gestión de la dotación esperada por turno de la estación seleccionada en el navbar:
// valor por defecto, valor por día de la semana y excepciones (día puntual, rango,
// rango + días de la semana).
export default function StaffingPage() {
  const { permissions } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const canRead = hasAnyRequiredPermission(permissions, STAFFING_PAGE_PERMISSIONS);
  const canWrite = hasAnyRequiredPermission(permissions, STAFFING_WRITE_PERMISSIONS);

  const { selectedStation } = useContext(StationContext);
  const stationId = selectedStation?.id ?? '';
  const [areaId, setAreaId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // cambios de dotación que todavía no se aplicaron a los calendarios abiertos
  const [pendingRange, setPendingRange] = useState(null);
  const [recalcOpen, setRecalcOpen] = useState(false);

  const { loading, refresh, principalWorkshifts, otherWorkshifts, areas, rules, defaultFor } = useStaffingData(stationId);
  const allWorkshifts = [...principalWorkshifts, ...otherWorkshifts];

  useEffect(() => {
    setAreaId((current) => (areas.some((a) => a.id === current) ? current : (areas[0]?.id ?? null)));
  }, [areas]);

  useEffect(() => { setPendingRange(null); }, [stationId]);

  const handleChanged = useCallback(async (suggestedRange) => {
    if (suggestedRange) setPendingRange((current) => mergeRecalcRanges(current, suggestedRange));
    try {
      await refresh();
      setRefreshKey((k) => k + 1);
    } catch {
      enqueueSnackbar('No se pudo actualizar la información de dotación.', { variant: 'error' });
    }
  }, [refresh, enqueueSnackbar]);

  if (!canRead) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">No tienes permiso para ver esta sección.</Typography>
      </Box>
    );
  }

  const area = areas.find((a) => a.id === areaId) || null;

  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" align="center" gutterBottom>Gestión de dotaciones</Typography>
      {selectedStation?.name && stationId && (
        <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 2 }}>
          {selectedStation.name}
          {!canWrite && ' · Solo lectura'}
        </Typography>
      )}

      {stationId && canWrite && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {pendingRange && (
            <Alert
              severity="warning"
              action={(
                <Stack direction="row" spacing={1}>
                  <Button color="inherit" size="small" onClick={() => setRecalcOpen(true)}>Aplicar a calendarios…</Button>
                  <Button color="inherit" size="small" onClick={() => setPendingRange(null)}>Descartar</Button>
                </Stack>
              )}
            >
              Los cambios ({describeRecalcRange(pendingRange)}) todavía no se aplicaron a los calendarios abiertos.
            </Alert>
          )}
          {!pendingRange && (
            <Box sx={{ textAlign: 'right' }}>
              <Button size="small" variant="outlined" onClick={() => setRecalcOpen(true)}>Aplicar a calendarios abiertos…</Button>
            </Box>
          )}
        </Stack>
      )}

      {!stationId && (
        <Typography color="text.secondary" align="center">Seleccioná una estación en la barra superior.</Typography>
      )}

      {stationId && areas.length > 0 && (
        <Tabs value={areaId ?? false} onChange={(_e, value) => setAreaId(value)} sx={{ mb: 2 }}>
          {areas.map((a) => <Tab key={a.id} value={a.id} label={a.name} />)}
        </Tabs>
      )}

      {stationId && !loading && areas.length === 0 && (
        <Typography color="text.secondary">Esta estación no tiene áreas de trabajo con dotación configurable.</Typography>
      )}

      {stationId && area && (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Por defecto y por día de la semana</Typography>
            <StaffingMatrix
              stationId={stationId} area={area} workshifts={principalWorkshifts} rules={rules}
              defaultFor={defaultFor} canWrite={canWrite} onChanged={handleChanged}
            />

            {otherWorkshifts.length > 0 && (
              <Accordion disableGutters variant="outlined" sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Otros turnos (opcional)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <StaffingMatrix
                    stationId={stationId} area={area} workshifts={otherWorkshifts} rules={rules}
                    defaultFor={defaultFor} canWrite={canWrite} onChanged={handleChanged}
                  />
                </AccordionDetails>
              </Accordion>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Excepciones</Typography>
            <StaffingExceptions
              stationId={stationId} area={area} workshifts={allWorkshifts} rules={rules}
              canWrite={canWrite} onChanged={handleChanged}
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Vista mensual</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <StaffingMonthPreview stationId={stationId} area={area} workshifts={allWorkshifts} refreshKey={refreshKey} />
          </Paper>
        </Stack>
      )}

      <StaffingRecalcDialog
        open={recalcOpen}
        onClose={() => setRecalcOpen(false)}
        stationId={stationId}
        initialRange={pendingRange}
        onApplied={() => setPendingRange(null)}
      />
    </Box>
  );
}
